import React, { useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Container from "../layout/Container";
import Reveal from "../motion/Reveal";
import CalendarVector from "../mockups/CalendarVector";
import AppVector from "../mockups/AppVector";
import DeskVector from "../mockups/DeskVector";
import { Cal, Rate, Reel } from "../characters/Character";
import { useI18n } from "../../i18n/I18nProvider";

const TABS = [
  { id: "calendar", tone: "bg-sky", Poster: CalendarVector, Character: Cal, label: "showcase.tabCalendar", title: "showcase.calendarTitle", body: "showcase.calendarBody" },
  { id: "app", tone: "bg-rose", Poster: AppVector, Character: Reel, label: "showcase.tabApp", title: "showcase.phoneTitle", body: "showcase.phoneBody" },
  { id: "desk", tone: "bg-mint", Poster: DeskVector, Character: Rate, label: "showcase.tabDesk", title: "showcase.monitorTitle", body: "showcase.monitorBody" },
];

const DURATION = 5000;

const Showcase = () => {
  const { t } = useI18n();
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const progressRef = useRef(0);
  const barRef = useRef(null);
  const active = TABS[index];

  const paint = (value) => {
    if (barRef.current) barRef.current.style.transform = `scaleX(${value})`;
  };

  const selectTab = (tabIndex) => {
    progressRef.current = 0;
    paint(reduced ? 1 : 0);
    setIndex(tabIndex);
  };

  useLayoutEffect(() => {
    if (reduced) {
      paint(1);
      return undefined;
    }
    if (paused) {
      paint(progressRef.current);
      return undefined;
    }

    let frame = 0;
    let last = performance.now();
    const tick = (now) => {
      const delta = now - last;
      last = now;
      progressRef.current = Math.min(1, progressRef.current + delta / DURATION);
      paint(progressRef.current);
      if (progressRef.current >= 1) {
        progressRef.current = 0;
        setIndex((current) => (current + 1) % TABS.length);
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [paused, reduced, index]);

  const onKeyDown = (event) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      selectTab((index + 1) % TABS.length);
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      selectTab((index - 1 + TABS.length) % TABS.length);
    }
    if (event.key === "Home") {
      event.preventDefault();
      selectTab(0);
    }
    if (event.key === "End") {
      event.preventDefault();
      selectTab(TABS.length - 1);
    }
  };

  return (
    <section id="product" className="scroll-mt-24 py-16 md:py-24">
      <Container>
        <Reveal>
          <p className="text-body font-medium text-accent">{t("showcase.kicker")}</p>
          <h2 className="mt-3 max-w-2xl text-heading font-bold tracking-heading text-ink md:text-heading-lg">
            {t("showcase.title")}
          </h2>
        </Reveal>
        <div
          className="mt-8"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <div
            role="tablist"
            aria-label={t("showcase.title")}
            className="flex flex-nowrap gap-2 overflow-x-auto sm:flex-wrap sm:overflow-visible"
            onKeyDown={onKeyDown}
          >
            {TABS.map((tab, tabIndex) => {
              const selected = tabIndex === index;
              return (
                <button
                  key={tab.id}
                  id={`tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls={`panel-${tab.id}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => selectTab(tabIndex)}
                  className="relative min-h-11 shrink-0 rounded-pill px-3 text-body font-medium text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:px-4"
                >
                  {selected ? (
                    <motion.span
                      layoutId="showcase-pill"
                      className="absolute inset-0 rounded-pill bg-lilac"
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    />
                  ) : null}
                  <span className={`relative ${selected ? "text-[#241c3e]" : "text-ink"}`}>
                    {t(tab.label)}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 h-1 overflow-hidden rounded-pill bg-lilac">
            <div ref={barRef} className="h-full origin-left scale-x-0 bg-accent" />
          </div>
          <div
            role="tabpanel"
            id={`panel-${active.id}`}
            aria-labelledby={`tab-${active.id}`}
            className="relative mt-6 min-h-[420px] overflow-hidden rounded-stage border border-hairline bg-paper p-6 shadow-card md:min-h-[560px] md:p-12"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={reduced ? false : { opacity: 0, x: 72 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduced ? undefined : { opacity: 0, x: -72 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="grid items-center gap-6 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]"
              >
                <div>
                  <h3 className="text-heading-sm font-semibold tracking-heading-sm text-ink">
                    {t(active.title)}
                  </h3>
                  <p className="mt-3 text-body text-mid-gray">{t(active.body)}</p>
                </div>
                <div className="relative mx-auto w-full max-w-lg pb-16">
                  <div
                    className={`pointer-events-none absolute left-[8%] top-[8%] z-0 h-[70%] w-[70%] rounded-full ${active.tone}`}
                    aria-hidden="true"
                  />
                  <div className="relative z-10">
                    <active.Poster />
                  </div>
                  <active.Character
                    size={64}
                    decorative
                    className="absolute bottom-0 left-0 z-10"
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Showcase;
